import { useEffect, useMemo } from 'react';
import { Link, useParams, useSearch } from 'wouter';
import { Button } from '@/components/ui/button';
import { FunnelLayout } from '@/pages/landing/shared/FunnelLayout';
import { SEOHead } from '@/pages/landing/shared/SEOHead';
import { TemplateLeadForm } from '@/pages/landing/shared/TemplateLeadForm';
import { SITE_URL } from '@/lib/brand';
import { saveBlob } from '@/lib/save-blob';
import { track } from '@/lib/track';
import { FUNNEL_COPY } from '@/marketing/funnel-copy';
import { FUNNEL_OFFER_ON, FUNNEL_PAGES_ON, funnelPages, readUtm, sheetImage, tradeName } from '@/marketing/funnel';
import { offerHref } from '@/marketing/funnel-offer';
import { isPrerendering } from '@/marketing/plans';
import { LEAD_FORM_ON, TURNSTILE_SITE_KEY } from '@/marketing/template-lead';
import { templatePdf } from '@/marketing/template-pdf';
import type { TemplatePage } from '@/marketing/templates';
import NotFoundPage from './not-found';

/*
 * /free/<template-slug>: the ad landing page of a template. The headline and promise, the free PDF, the Google Sheet by email (when that form is on) and
 * one call to action, in a layout with no navigation (FunnelLayout). noindex, follow; an unknown slug, or a build without VITE_FUNNEL_PAGES, is the
 * not-found page. Analytics events carry the template slug and a step, never an email address or the query string.
 */
function Page({ p }: { p: TemplatePage }) {
  const url = `${SITE_URL}/free/${p.slug}`;
  const doc = p.blocks.find((b) => b.type === 'document');
  // the ad source is sent with the email form and, for the offer, as the pricing link's utm_source: never written into the page
  const search = useSearch();
  const utm = useMemo(() => readUtm(search), [search]);
  useEffect(() => {
    if (!isPrerendering()) track('Funnel page', { template: p.slug, step: 'viewed' });
  }, [p.slug]);
  const download = () => {
    if (doc?.type !== 'document') return;
    track('Funnel page', { template: p.slug, step: 'pdf' });
    saveBlob(templatePdf(p.h1, doc), `${doc.pdfName}.pdf`);
  };
  const trade = tradeName(p.slug);
  const heading = FUNNEL_COPY.hero.title(trade);
  const offer = FUNNEL_COPY.signup.offer;
  const arrow = (
    <svg aria-hidden="true" viewBox="0 0 24 24" className="funnel-bob h-8 w-8 shrink-0 text-amber-400 sm:h-12 sm:w-12" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 4v15m0 0-6-6m6 6 6-6" />
    </svg>
  );
  return (
    <FunnelLayout>
      <SEOHead title={heading} description={p.metaDescription} canonical={url} robots="noindex, follow" />
      <article>
        <section className="bg-white px-4 py-10 sm:px-6 sm:py-16">
          <div className="mx-auto max-w-3xl rounded-3xl border-[6px] border-amber-400 bg-white px-4 py-10 text-center sm:px-10 sm:py-14">
            <h1 className="mb-8 flex items-center justify-center gap-3 text-4xl font-extrabold leading-tight text-slate-900 sm:gap-5 sm:text-6xl">
              {arrow}
              <span>{heading}</span>
              {arrow}
            </h1>
            <Button type="button" size="lg" className="min-h-[56px] px-8 text-lg" onClick={download}>{FUNNEL_COPY.hero.button}</Button>
          </div>
        </section>

        {/* the literal switch check lets the build drop the form (and Turnstile's loader) from the bundle when the switch is off */}
        {import.meta.env.VITE_TEMPLATE_LEAD_FORM === 'true' && LEAD_FORM_ON && p.leadSheet && TURNSTILE_SITE_KEY && (
          <section className="bg-slate-100 py-12 sm:py-16" aria-labelledby="funnel-sheet">
            <div className="mx-auto grid max-w-6xl items-center gap-8 px-4 sm:px-6 lg:grid-cols-2 lg:gap-14">
              <div>
                <h2 id="funnel-sheet" className="text-center text-3xl font-bold text-slate-900 sm:text-4xl lg:text-left">{FUNNEL_COPY.sheet.heading}</h2>
                <TemplateLeadForm key={p.slug} template={p.slug} page="funnel" utm={utm} labelledBy="funnel-sheet" notice={FUNNEL_COPY.sheet.notice} />
              </div>
              <img
                src={sheetImage(p.slug)}
                alt={FUNNEL_COPY.sheet.imageAlt(trade)}
                width={1022}
                height={668}
                loading="lazy"
                decoding="async"
                className="h-auto w-full rounded-xl border border-slate-300 bg-white shadow-xl"
              />
            </div>
          </section>
        )}

        <section className="bg-slate-900 py-14 text-center text-white sm:py-20">
          <div className="mx-auto max-w-2xl px-4 sm:px-6">
            <h2 className="mb-3 text-2xl font-bold sm:text-4xl" style={{ color: '#fff' }}>{FUNNEL_COPY.signup.heading}</h2>
            <p className="mb-6 text-xl text-slate-200 sm:text-2xl">{FUNNEL_COPY.signup.lead}</p>
            {FUNNEL_OFFER_ON && (
              <div className="mb-8" data-funnel-offer>
                <p className="mb-2 text-3xl font-extrabold sm:text-5xl">
                  {offer.trialBefore}
                  <span className="text-amber-400">{offer.free}</span>
                </p>
                <p className="mb-4 text-xl font-semibold sm:text-2xl">{offer.noCard}</p>
                <p className="mb-4 text-3xl font-extrabold sm:text-5xl">{offer.discount}</p>
                <p className="mx-auto max-w-md text-base text-slate-300 sm:text-lg">{offer.small}</p>
              </div>
            )}
            <Link href={FUNNEL_OFFER_ON ? offerHref(utm.utm_source) : p.cta.href} className="btn btn-y" onClick={() => track('Funnel page', { template: p.slug, step: 'cta' })}>{FUNNEL_COPY.signup.button}</Link>
          </div>
        </section>
      </article>
    </FunnelLayout>
  );
}

export default function FunnelRoute() {
  const { slug } = useParams<{ slug: string }>();
  const p = FUNNEL_PAGES_ON ? funnelPages().find((t) => t.slug === slug) : undefined;
  return p ? <Page p={p} /> : <NotFoundPage />;
}
